// ---------------------------------------------------------------------------
// Value features — a position as numbers, for the learned win probability
// ---------------------------------------------------------------------------
//
// valueFeatures(result, seat) → a fixed-length array describing the position
// after a round (resolveRound's players: { board, homefront, yield,
// structuresLost }) from `seat`'s side. The same function feeds the training
// data (scripts/value-data.js) and the search at play time
// (bots/valueModel.js), so the model always sees what it was trained on.
//
// Every quantity comes in a pair — ours, then theirs — so the model can
// learn that a thing is good for us and bad for them. FEATURE_NAMES labels
// them, in order.

import { rules } from '../config.js'
import { basesOf, calculateCombatStrength, eggLays } from '../gameLogic.js'
import { isStructureDestroyed } from '../subplotGrid.js'
import { baseFootprint } from '../subplotBases.js'
import { viewOfOpponentGrid } from './view.js'
import { tickYield } from '../subplotEconomy.js'
import { legalThreats, reachOf, stakeInReach } from './threats.js'

const PAIRED = [
    'structuresLost', 'matchPoint', 'liveCells', 'oneCellStructures',
    'drones', 'freshDrones', 'edgeSum', 'bigEdges',
    'broodsBuilding', 'broodsMature', 'eggsReady',
    'buildingExposed1', 'buildingExposed2', 'structuresExposed',
    'uncoveredThreat', 'strikeCorridors',
    'intelKnown', 'intelStamps', 'rockets', 'yield',
    // Added for the second model: what a strike we can't stop could reach,
    // weighted as the deploys weigh it (threats.stakeInReach: a one-cell
    // structure 1.5×, at match point 3×) — the most in any one corridor, and
    // in all.
    'maxExposedStake', 'exposedStake',
    // Added for rules v8 (Gary's model): kills to win vary by war, Hives by
    // arena, and lots pay the yield — the kills still to take before this
    // side loses, its yield per round, and its structures standing.
    'killsLeft', 'yieldRate', 'standing',
]
export const FEATURE_NAMES = [...PAIRED.flatMap(n => [`my_${n}`, `opp_${n}`]), 'round']

const strength = (bf) => calculateCombatStrength(bf) + (bf.newArmies || 0) + (bf.movedInArmies || 0)

// One side's numbers; `them` is the other side.
function sideFeatures(me, them, round) {
    const hf = typeof me.homefront === 'string' ? JSON.parse(me.homefront) : me.homefront
    const toWin = rules().structuresToWin
    const lost = me.structuresLost ?? 0
    let liveCells = 0, oneCell = 0
    const liveStructureCells = []
    for (const [id, st] of Object.entries(hf?.structures ?? {})) {
        if (isStructureDestroyed(hf, id)) continue
        const live = st.footprint.filter(s => !hf.subplots[s].destroyed)
        liveCells += live.length
        if (live.length === 1) oneCell++
        liveStructureCells.push(live)
    }
    let drones = 0, fresh = 0, edgeSum = 0, bigEdges = 0
    me.board.forEach((bf, c) => {
        drones += (bf.armyQty || 0) + (bf.newArmies || 0) + (bf.movedInArmies || 0)
        fresh += bf.newArmies || 0
        const d = strength(bf) - strength(them.board[c])
        if (d > 0) edgeSum += d
        if (d >= 2) bigEdges++
    })
    let building = 0, mature = 0, eggs = 0
    for (const bf of me.board) for (const b of basesOf(bf)) {
        if (b.mature) mature++; else building++
        if (eggLays(b)) eggs++
    }
    // Exposure to the other side's legal strikes next round.
    const threat = legalThreats(them.board)
    const short = me.board.map((bf, k) => threat[k] - strength(bf))
    let exposed1 = 0, exposed2 = 0
    for (let c = 0; c < me.board.length; c++) for (const b of basesOf(me.board[c])) {
        if (b.mature) continue
        const fp = baseFootprint(b.centerS) ?? []
        let edge = 0
        for (const k of [c - 1, c, c + 1]) {
            if (!me.board[k] || short[k] <= 0) continue
            if (fp.some(s => reachOf(k, short[k]).has(s))) edge = Math.max(edge, short[k])
        }
        if (edge === 1) exposed1++
        if (edge >= 2) exposed2++
    }
    let structuresExposed = 0, uncovered = 0, strikeCorridors = 0, maxStake = 0, stakeSum = 0
    threat.forEach((t, k) => {
        if (t > 0) strikeCorridors++
        if (short[k] <= 0) return
        uncovered += short[k]
        const st = stakeInReach(hf, me.board, k, short[k], lost)
        maxStake = Math.max(maxStake, st)
        stakeSum += st
        const reach = reachOf(k, short[k])
        structuresExposed += liveStructureCells.filter(cells => cells.some(s => reach.has(s))).length
    })
    // What this side knows of the other's Grid.
    const g = viewOfOpponentGrid(them.homefront)
    const known = g.cells.filter(c => c.known !== 'unknown').length
    const stamps = g.stamps.filter(t => !t.dead && t.distance != null).length
    let rockets = 0
    for (const st of Object.values(hf?.structures ?? {})) {
        rockets += (st.rocketCells || []).filter(s => !hf.subplots[s]?.destroyed).length
    }
    void round
    const standing = liveStructureCells.length
    return [
        lost, lost >= toWin - 1 ? 1 : 0, liveCells / 10, oneCell,
        drones / 5, fresh / 2, edgeSum / 5, bigEdges,
        building, mature, eggs,
        exposed1, exposed2, structuresExposed,
        uncovered / 5, strikeCorridors,
        known / 50, stamps, rockets, (me.yield ?? 0) / 100,
        maxStake, stakeSum / 3,
        Math.max(0, toWin - lost), hf ? tickYield(hf).yieldIncrement / 10 : 0, standing,
    ]
}

export function valueFeatures(result, seat, round = 0) {
    const other = seat === 'p1' ? 'p2' : 'p1'
    const mine = sideFeatures(result[seat], result[other], round)
    const theirs = sideFeatures(result[other], result[seat], round)
    return [...mine.flatMap((v, i) => [v, theirs[i]]), Math.min(round, 40) / 20]
}
